import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import swal from 'sweetalert'

import { CatchError } from 'Exceptions/exceptions'
import { EMPTY_FORM_INQUIRY_CONFIG } from 'Main/Constant'
import InquiryConfigService from 'Main/service/InquiryConfigsService'
import CommonListApiPaginated from 'views/components/Table/CommonListApiPaginated'
import { getRefreshToken } from 'variables/common'

function InquiryConfigList () {
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const inquiryConfigService = useMemo(() => new InquiryConfigService(), [])
  const [reloadKey, setReloadKey] = useState(0)
  const [paymentVendors, setPaymentVendors] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [formData, setFormData] = useState(EMPTY_FORM_INQUIRY_CONFIG)

  const getConfigs = useCallback(() => inquiryConfigService.getData(), [inquiryConfigService])

  const handleListError = useCallback((error) => {
    CatchError(error, navigate, refreshToken)
  }, [navigate, refreshToken])

  useEffect(() => {
    inquiryConfigService
      .getPaymentVendors()
      .then((response) => setPaymentVendors(response?.data || response || []))
      .catch(() => setPaymentVendors([]))
  }, [inquiryConfigService])

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleOpenAddModal = () => {
    setIsEdit(false)
    setFormData(EMPTY_FORM_INQUIRY_CONFIG)
    setShowModal(true)
  }

  const handleOpenEditModal = useCallback(async (id) => {
    try {
      const response = await inquiryConfigService.getById(id)
      const config = response?.data || response

      if (!config?.id) {
        swal('Error', 'Inquiry Config Not Found', 'error')
        return
      }

      setIsEdit(true)
      setFormData({
        id: config.id,
        name: config.name || '',
        payment_vendors_id: config.payment_vendors_id || '',
        prefix: config.prefix || '',
        callback_url: config.callback_url || '',
        description: config.description || '',
        enabled: Boolean(config.enabled)
      })
      setShowModal(true)
    } catch (error) {
      CatchError(error, navigate, refreshToken)
    }
  }, [inquiryConfigService, navigate, refreshToken])

  const handleSubmitForm = async (event) => {
    event.preventDefault()

    try {
      setSubmitting(true)
      const payload = {
        name: formData.name,
        payment_vendors_id: formData.payment_vendors_id,
        prefix: formData.prefix,
        callback_url: formData.callback_url,
        description: formData.description,
        enabled: formData.enabled
      }

      if (isEdit) {
        await inquiryConfigService.update(formData.id, payload)
        await swal('Success!', 'Inquiry Config Updated', 'success')
      } else {
        await inquiryConfigService.create(payload)
        await swal('Success!', 'Inquiry Config Added', 'success')
      }

      setShowModal(false)
      setReloadKey((current) => current + 1)
    } catch (error) {
      const caught = CatchError(error, navigate, refreshToken)
      if (caught !== undefined) swal('Error', 'Request failed! Please check your input data.', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = useCallback((id) => {
    swal({
      title: 'Delete Inquiry Config',
      text: 'Are you sure you want to delete this Inquiry Config?',
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true
    }).then(async (willDelete) => {
      if (!willDelete) return

      try {
        await inquiryConfigService.deleteById(id)
        await swal('Success!', 'Inquiry Config Deleted', 'success')
        setReloadKey((current) => current + 1)
      } catch (error) {
        CatchError(error, navigate, refreshToken)
      }
    })
  }, [inquiryConfigService, navigate, refreshToken])

  const getVendorName = useCallback((vendorId) => {
    const vendor = paymentVendors.find((item) => String(item.id) === String(vendorId))
    return vendor ? vendor.name : vendorId
  }, [paymentVendors])

  const columns = useMemo(() => [
    { header: 'ID', accessorKey: 'id' },
    { header: 'Name', accessorKey: 'name' },
    { header: 'Prefix', accessorKey: 'prefix' },
    { header: 'Callback Url', accessorKey: 'callback_url' },
    {
      header: 'Vendor Gateway',
      accessorKey: 'payment_vendors_id',
      cell: ({ getValue }) => getVendorName(getValue())
    },
    {
      header: 'Enabled',
      accessorKey: 'enabled',
      cell: ({ getValue }) => getValue()
        ? <span className="status-success">Enabled</span>
        : <span className="status-danger">Disabled</span>
    },
    {
      header: 'Action',
      id: 'actions',
      enableSorting: false,
      filterable: false,
      exportable: false,
      cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button type="button" onClick={() => handleOpenEditModal(row.original.id)} className="btn btn-primary" title="Edit Inquiry Config">
            <i className="fa-solid fa-pen-to-square" />
          </button>
          <button type="button" onClick={() => handleDelete(row.original.id)} className="btn btn-danger" title="Delete Inquiry Config">
            <i className="fa-solid fa-trash-can" />
          </button>
        </div>
      )
    }
  ], [getVendorName, handleDelete, handleOpenEditModal])

  const menu = (
    <div className="text-center">
      <button type="button" onClick={handleOpenAddModal} className="btn btn-success" title="Tambah Inquiry Config">
        <i className="fa-solid fa-pen-to-square" />&nbsp;&nbsp; Tambah Inquiry Config
      </button>
    </div>
  )

  return (
    <div className="page-content">
      <section>
        <CommonListApiPaginated
          header="List Inquiry Config"
          subtitle=""
          columns={columns}
          getData={getConfigs}
          menu={menu}
          reloadKey={reloadKey}
          loadingLabel="Loading inquiry configs..."
          emptyLabel="No inquiry configs found."
          onError={handleListError}
        />
      </section>

      {showModal && (
        <>
          <div className="modal-backdrop fade show" onClick={submitting ? undefined : () => setShowModal(false)} />
          <div className="modal fade show" tabIndex="-1" style={{ display: 'block' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <form onSubmit={handleSubmitForm}>
                  <div className="modal-header">
                    <h5 className="modal-title">{isEdit ? 'Edit Inquiry Config' : 'Add Inquiry Config'}</h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowModal(false)} disabled={submitting} />
                  </div>

                  <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <div className="mb-2">
                      <label htmlFor="qc_name" className="form-label">Name</label>
                      <input id="qc_name" name="name" type="text" className="form-control" placeholder="Config Name" value={formData.name} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-2">
                      <label htmlFor="qc_payment_vendors_id" className="form-label">Vendor Gateway</label>
                      <select id="qc_payment_vendors_id" name="payment_vendors_id" className="form-control" value={formData.payment_vendors_id} onChange={handleInputChange} required>
                        <option value="">-- Select Vendor Gateway --</option>
                        {paymentVendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}
                      </select>
                    </div>
                    <div className="mb-2">
                      <label htmlFor="qc_prefix" className="form-label">Prefix</label>
                      <input id="qc_prefix" name="prefix" type="text" className="form-control" placeholder="Prefix" value={formData.prefix} onChange={handleInputChange} />
                    </div>
                    <div className="mb-2">
                      <label htmlFor="qc_callback_url" className="form-label">Callback Url</label>
                      <input id="qc_callback_url" name="callback_url" type="text" className="form-control" placeholder="https://..." value={formData.callback_url} onChange={handleInputChange} />
                    </div>
                    <div className="mb-2">
                      <label htmlFor="qc_description" className="form-label">Description</label>
                      <textarea id="qc_description" name="description" className="form-control" placeholder="Description" rows="3" value={formData.description} onChange={handleInputChange} />
                    </div>
                    <div className="form-check mt-3 mb-2">
                      <input className="form-check-input" type="checkbox" id="qc_enabled" name="enabled" checked={formData.enabled} onChange={handleInputChange} />
                      <label className="form-check-label" htmlFor="qc_enabled">Enabled</label>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-light text-black" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</button>
                    <button type="submit" className="btn btn-success" disabled={submitting}>
                      <i className={`fa-solid ${submitting ? 'fa-circle-notch fa-spin' : 'fa-floppy-disk'}`} />{' '}
                      {submitting ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Inquiry Config')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default InquiryConfigList
